import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, resolveDirective as _resolveDirective, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { id: "container" }
const _hoisted_2 = { id: "anakin-loading" }

import {
  ref, reactive, watch, watchEffect, onMounted, computed, defineAsyncComponent,
} from 'vue';
import { useFavicon, useTitle } from '@vueuse/core';
import { type GProduct } from '@daocloud-proto/ghippo/v1alpha1/gproduct.pb';
import {
  prefetchApps,
  registerMicroApps, setDefaultMountApp, start, type RegistrableApp,
} from 'qiankun';
import AnakinNav from '@/components/AnakinNav.vue';
import AnakinHeader from '@/components/AnakinHeader.vue';
import {
  ProductNavigator,
  type ProductNavCategory,
  type ShortcutMenus,
} from '@daocloud-proto/ghippo/v1alpha1/productnav.pb';
import { TopNavigator } from '@daocloud-proto/ghippo/v1alpha1/topnav.pb';
import getProductListInfo from '@/utils/productListInfo';
import {
  registerGlobalStoreHandler,
  registerLoadLanguageAsyncHandler,
  clearLoadLanguageAsyncHandler,
  registerErrorHandlers,
  clearErrorHandlers,
  useGlobalStore,
} from '@/stores';
import { useI18n } from 'vue-i18n';
import DaoCloudIconFont from '@dao-style/core/dist/font/DaoCloudIconFont/DaoCloudIconFont.woff2';
import { simpleErrorNoty } from '@/utils/noty';
import LicenseExpire from './components/LicenseExpire.vue';
import pathJoin from './utils/pathJoin';
import getUrlBase from './utils/getUrlBase';
import getLicensesOverQuota from './utils/getLicensesOverQuota';
import SystemMessage from './components/SystemMessage.vue';
import TopNav from './components/TopNav.vue';
import { fetchHandler, getTemplateHandler } from './utils/prefetchOverride';
import useUserStore from './stores/user';

// LicenseActivate 中包含 monaco-editor 尺寸大用处少故异步加载
type RegistrableConfig = RegistrableApp<Record<string, unknown>>;


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const LicenseActivate = defineAsyncComponent(() => import('@/components/LicenseActivate.vue'));

const { t } = useI18n();

const store = useGlobalStore();
const userStore = useUserStore();

const categories = ref<ProductNavCategory[]>([]);
const shortcutMenus = ref<ShortcutMenus>({});

const isCSP = computed(
  () => !!(!categories.value.length
  && (shortcutMenus.value.left?.length || shortcutMenus.value.right?.length)),
);

const getNavInfo = async () => {
  try {
    const res = await ProductNavigator.Info({});

    categories.value = res.categories || [];
    shortcutMenus.value = res.shortcutMenus || {};
  } catch (e) {
    simpleErrorNoty(e, {
      name: t('App.navInfo'),
    });
  }
};
// 提前请求nav信息
const navInfoRes = getNavInfo();

const updateNavInfo = async () => Promise.all([getNavInfo(), getProductListInfo()]);

let hasListener = false;
const languagechangeListener = async () => {
  await store.setLocale('');
  updateNavInfo();
};
const handlerLanguageChange = () => {
  // 如果local为''或undefined，会走浏览器语言。监听浏览器语言切换事件，随之切换。否则取消监听
  if (!store.locale) {
    if (!hasListener) {
      hasListener = true;
      window.addEventListener('languagechange', languagechangeListener);
    }
  } else {
    window.removeEventListener('languagechange', languagechangeListener);
    hasListener = false;
  }
};

handlerLanguageChange();

// nav和product信息依赖i18n
watch(() => store.locale, () => {
  updateNavInfo();
  handlerLanguageChange();
});

useFavicon(computed(() => store.navConfig.favicon));

const getNavConfig = async () => {
  try {
    const navConfig = await TopNavigator.Info({});

    store.setNavConfig(navConfig);
  } catch (e) {
    simpleErrorNoty(e, {
      name: t('App.topNav'),
    });
  }
};

getNavConfig();

let apps: RegistrableConfig[] = [];

const loadingProps = reactive({
  visible: false,
  title: computed(() => t('common.loading')),
  type: 'dot',
});

const loader = (loading: boolean) => {
  loadingProps.visible = loading;
};

const globalProps = {
  registerErrorHandlers,
  registerGlobalStoreHandler,
  registerLoadLanguageAsyncHandler,
};

if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hostname } = window.location;

  apps = [
    {
      name: process.env.VUE_APP_ACTIVE_APP_NAME, // app name registered
      entry: process.env.VUE_APP_ENTRY,
      activeRule: process.env.VUE_APP_ACTIVE_RULE,
    },
    // {
    //   name: 'kpanda', // app name registered
    //   entry: `//${hostname}:8100/`,
    //   activeRule: '/kpanda',
    // },
    // {
    //   name: 'insight',
    //   entry: `//${hostname}:8110/`,
    //   activeRule: '/insight',
    // },
    // {
    //   name: 'mspider',
    //   entry: `//${hostname}:8120/`,
    //   activeRule: '/mspider',
    // },
    // {
    //   name: 'skoala',
    //   entry: `//${hostname}:8130/`,
    //   activeRule: '/skoala',
    // },
    // {
    //   name: 'ghippo',
    //   entry: `//${hostname}:8140/`,
    //   activeRule: '/ghippo',
    // },
    // {
    //   name: 'mcamel-seaweedfs',
    //   entry: `//${hostname}:8150/`,
    //   activeRule: '/mcamel/seaweedfs',
    // },
    // {
    //   name: 'mcamel-rabbitmq',
    //   entry: `//${hostname}:8151/`,
    //   activeRule: '/mcamel/rabbitmq',
    // },
    // {
    //   name: 'mcamel-elasticsearch',
    //   entry: `//${hostname}:8152/`,
    //   activeRule: '/mcamel/elasticsearch',
    // },
    // {
    //   name: 'mcamel-mysql',
    //   entry: `//${hostname}:8153/`,
    //   activeRule: '/mcamel/mysql',
    // },
    // {
    //   name: 'mcamel-redis',
    //   entry: `//${hostname}:8154/`,
    //   activeRule: '/mcamel/redis',
    // },
    // {
    //   name: 'mcamel-kafka',
    //   entry: `//${hostname}:8155/`,
    //   activeRule: '/mcamel/kafka',
    // },
    // {
    //   name: 'mcamel-minio',
    //   entry: `//${hostname}:8156/`,
    //   activeRule: '/mcamel/minio',
    // },
    // {
    //   name: 'mcamel-postgresql',
    //   entry: `//${hostname}:8157/`,
    //   activeRule: '/mcamel/postgresql',
    // },
    // {
    //   name: 'mcamel-mongodb',
    //   entry: `//${hostname}:8158/`,
    //   activeRule: '/mcamel/mongodb',
    // },
    // {
    //   name: 'mcamel-rocketmq',
    //   entry: `//${hostname}:8159/`,
    //   activeRule: '/mcamel/rocketmq',
    // },
    // {
    //   name: 'amamba',
    //   entry: `//${hostname}:8160/`,
    //   activeRule: '/amamba',
    // },
    // {
    //   name: 'ipavo',
    //   entry: `//${hostname}:8170/`,
    //   activeRule: '/ipavo',
    // },
    // {
    //   name: 'kairship',
    //   entry: `//${hostname}:8180/`,
    //   activeRule: '/kairship',
    // },
    // {
    //   name: 'kangaroo',
    //   entry: `//${hostname}:8190/`,
    //   activeRule: '/kangaroo',
    // },
    // {
    //   name: 'spidernet',
    //   entry: `//${hostname}:8200/`,
    //   activeRule: '/spidernet',
    // },
    // {
    //   name: 'kcoral',
    //   entry: `//${hostname}:8210/`,
    //   activeRule: '/kcoral',
    // },
    // {
    //   name: 'kcollie',
    //   entry: `//${hostname}:8220/`,
    //   activeRule: '/kcollie',
    // },
    // {
    //   name: 'gmagpie',
    //   entry: `//${hostname}:8230/`,
    //   activeRule: '/gmagpie',
    // },
    // {
    //   name: 'kant',
    //   entry: `//${hostname}:8240/`,
    //   activeRule: '/kant',
    // },
    // {
    //   name: 'dowl',
    //   entry: `//${hostname}:8250/`,
    //   activeRule: '/dowl',
    // },
    // {
    //   name: 'virtnest',
    //   entry: `//${hostname}:8260/`,
    //   activeRule: '/virtnest',
    // },
    // {
    //   name: 'kolm',
    //   entry: `//${hostname}:8270/`,
    //   activeRule: '/kolm',
    // },
    // {
    //   name: 'baize',
    //   entry: `//${hostname}:8280/`,
    //   activeRule: '/baize',
    // },
    // {
    //   name: 'leopard',
    //   entry: `//${hostname}:8290/`,
    //   activeRule: '/leopard',
    // },
  ].map((app) => Object.assign(app, {
    container: '#container',
    loader,
    props: {
      ...globalProps,
      basePath: pathJoin(getUrlBase(), app.activeRule),
    },
  }));
} else {
  // 一份正常的qiankun子应用信息应该长这样👇
  // const hardCodeRules = [
  //   {
  //     name: 'kpanda', // app name registered
  //     entry: '/ui/kpanda/', // 结尾的/是必须的
  //     container: '#container',
  //     activeRule: '/kpanda',
  //     loader,
  //     props: globalProps,
  //   },
  // ];

  apps = store.productList.reduce((appInfo, {
    id,
    uiAssetsUrl,
    url,
  }) => {
    if (!id || !uiAssetsUrl || !url) {
      console.warn(`${id} product info is not complete`);
    } else {
      appInfo.push({
        name: id,
        entry: uiAssetsUrl,
        container: '#container',
        activeRule: url,
        loader,
        props: {
          ...globalProps,
          basePath: url,
        },
      });
    }

    return appInfo;
  }, [] as RegistrableConfig[]);
}

// needImportLicense 有没有激活
// needUpdateExpiredLicense 这个字段在已过期且需要开启强弹窗提示时为true
const activationInfo = computed(() => store.productList.reduce(
  (info, { id, needImportLicense, needUpdateExpiredLicense }) => info.set(id, {
    needImport: needImportLicense,
    needUpdate: needUpdateExpiredLicense,
  }),
  new Map<GProduct['id'], {
    needImport: GProduct['needImportLicense'],
    needUpdate: GProduct['needUpdateExpiredLicense']
  }>(),
));

const ghippoName = 'ghippo';
const ghippoNeedLicense = computed(() => !!activationInfo.value.get(ghippoName)?.needImport);
const activeAppNeedLicense = computed(
  () => !!activationInfo.value.get(store.activeAppName)?.needImport,
);
const needLicenseAppName = computed(
  () => (ghippoNeedLicense.value ? ghippoName : store.activeAppName),
);

const needUpdateLicense = computed(
  () => activationInfo.value.get(needLicenseAppName.value)?.needUpdate,
);

watch(() => store.activeAppName, () => {
  getProductListInfo();
  store.title = '';
});

const onConfirm = () => {
  getProductListInfo();
  getLicensesOverQuota();
};

const initQiankun = (defaultLink: string | undefined) => {
  if (defaultLink) {
    setDefaultMountApp(defaultLink);
  } else {
    throw new Error('default link is not set');
  }

  registerMicroApps(apps, {
    // 类型要求必须是 async
    beforeMount: async (app) => {
      console.log(`ready to mount ${app.name}`);
      store.setActiveAppName(app.name);
    },
    beforeUnmount: async ({ name }) => {
      clearLoadLanguageAsyncHandler(name);
      clearErrorHandlers(name);
    },
  });

  const documentTitle = useTitle();

  // title跟随i18n信息和子应用改变
  watchEffect(() => {
    const { activeAppName } = store;
    const { tabName } = store.navConfig;

    // 直接进入父应用未激活子应用的时候，用默认title
    if (!activeAppName) {
      documentTitle.value = tabName;
    } else {
      const { title } = store.productList.find(({ id }) => id === activeAppName) ?? {};

      documentTitle.value = `${store.title || title || activeAppName} - ${tabName}`;
    }
  });

  start({
    sandbox: {
      experimentalStyleIsolation: true,
    },
    prefetch: false,
    fetch: fetchHandler,
    // 去除子应用中的favicon防止在Firefox中覆盖父应用的favicon
    getTemplate: getTemplateHandler,
  });
};

// DaoCloudIconFont 会偶发神秘失效，监控加载状态
const logDaoCloudIconFontState = async () => {
  const { href: fontUrl } = new URL(
    DaoCloudIconFont,
    window.location.protocol + window.location.host + getUrlBase(),
  );
  const fontFile = new FontFace('DaoCloudIconFont', `url(${fontUrl})`);

  try {
    await fontFile.load();
    console.log('DaoCloudIconFont state', fontFile);
  } catch (e) {
    console.error('DaoCloudIconFont load failed! state:', fontFile, 'fontFile.load', e);
  }
};

const defaultLink = ref<string>();

const getDefaultLink = () => {
  const link = categories.value.find(({ menus = [] }) => menus[0]?.url)?.menus?.[0]?.url;

  if (link) {
    return link;
  }

  return (shortcutMenus.value.left ?? []).concat(
    shortcutMenus.value.right ?? [],
  ).find((item) => item?.url)?.url;
};

onMounted(async () => {
  if (process.env.NODE_ENV === 'development') {
    defaultLink.value = String(apps[0].activeRule);
  } else {
    await navInfoRes;
    defaultLink.value = getDefaultLink();
  }
  initQiankun(defaultLink.value);
  logDaoCloudIconFontState();
});

const getParentElement = (element: HTMLElement, tagName: string): HTMLElement | undefined => {
  if (element.tagName === tagName) {
    return element;
  }

  if (element.tagName === 'BODY' || !element.parentElement) {
    return undefined;
  }

  return getParentElement(element.parentElement, tagName);
};

document.addEventListener('mouseover', (event) => {
  const target = getParentElement(event.target as HTMLElement, 'A');

  if (!target) {
    return;
  }

  const link = target.getAttribute('href');

  if (!link) {
    return;
  }

  const basePath = getUrlBase();
  const product = store.getProductInfoByUrl(basePath + link.substring(2));

  if (!product || !product.id || !product.uiAssetsUrl) {
    return;
  }
  prefetchApps([{
    name: product.id,
    entry: product.uiAssetsUrl,
  }], {
    fetch: fetchHandler,
    getTemplate: getTemplateHandler,
  });
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(SystemMessage),
    _createVNode(LicenseExpire),
    _createVNode(AnakinHeader, {
      class: "ghippo-header",
      "portal-url": defaultLink.value,
      "is-csp": _unref(isCSP),
      "show-sub-account": _unref(userStore).showSubAccount
    }, {
      "top-nav": _withCtx(() => [
        _createVNode(TopNav, { "shortcut-menus": shortcutMenus.value }, null, 8, ["shortcut-menus"])
      ]),
      default: _withCtx(() => [
        (categories.value.length)
          ? (_openBlock(), _createBlock(AnakinNav, {
              key: 0,
              navs: categories.value
            }, null, 8, ["navs"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["portal-url", "is-csp", "show-sub-account"]),
    (_unref(activeAppNeedLicense) || _unref(ghippoNeedLicense) || _unref(needUpdateLicense))
      ? (_openBlock(), _createBlock(_unref(LicenseActivate), {
          key: 0,
          product: _unref(needLicenseAppName),
          "need-update-license": _unref(needUpdateLicense),
          onConfirm: onConfirm
        }, null, 8, ["product", "need-update-license"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", _hoisted_1, null, 512), [
      [_vShow, !loadingProps.visible]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_2, null, 512), [
      [_vShow, loadingProps.visible],
      [_directive_loading, loadingProps]
    ])
  ], 64))
}
}

})